/**
 * useAdminNotificationStream
 * Connects to /api/notifications/stream (SSE) and returns the latest
 * admin notification feed in real time.
 *
 * Falls back to the last known data on disconnect; the browser's native
 * EventSource reconnects automatically with exponential back-off.
 *
 * Pass `enabled={false}` (or omit when no user is logged in) to prevent
 * connecting before authentication — avoids infinite error/setState loops.
 */
import { useState, useEffect, useRef } from "react";

export type FeedItem = {
  id: string;
  kind: "push" | "task" | "file";
  title: string;
  message: string;
  senderName: string;
  isRead: boolean;
  createdAt: Date;
  meta: Record<string, string | number | boolean | null>;
};

type StreamState = {
  feed: FeedItem[];
  unreadCount: number;
  connected: boolean;
};

const EMPTY_STATE: StreamState = { feed: [], unreadCount: 0, connected: false };

export function useAdminNotificationStream(enabled = true): StreamState {
  const [state, setState] = useState<StreamState>(EMPTY_STATE);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    // Do not connect until the user is authenticated — prevents infinite
    // error→setState→reconnect loops on fresh/unauthenticated page loads.
    if (!enabled) {
      setState(EMPTY_STATE);
      return;
    }

    let cancelled = false;

    function connect() {
      if (cancelled) return;
      const es = new EventSource("/api/notifications/stream");
      esRef.current = es;

      es.addEventListener("feed", (e: MessageEvent) => {
        if (cancelled) return;
        try {
          const raw: FeedItem[] = JSON.parse(e.data);
          // Deserialise Date strings → Date objects
          const feed = raw.map((item) => ({
            ...item,
            createdAt: new Date(item.createdAt),
          }));
          const unreadCount = feed.filter((f) => !f.isRead).length;
          setState({ feed, unreadCount, connected: true });
        } catch {
          // Malformed payload — ignore
        }
      });

      es.addEventListener("error", () => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, connected: false }));
        es.close();
        // Reconnect after 5 s (EventSource already retries, but we force
        // a clean reconnect to reset the connection state)
        setTimeout(connect, 5_000);
      });

      es.addEventListener("open", () => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, connected: true }));
      });
    }

    connect();

    return () => {
      cancelled = true;
      esRef.current?.close();
      esRef.current = null;
    };
  }, [enabled]);

  return state;
}
